import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccountingDataTable from '../../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../../accounting/shared/AccountingDataTable';
import BillingService, {
  InvoiceIssuerCompany,
  SaveInvoiceIssuerCompany,
} from '../../../services/BillingService';
import SuperAdminService, { Company } from '../../../services/SuperAdminService';
import { PAYMENT_SERVICE_URL } from '../../../config/api';
import GuideButton from '../../common/guide/GuideButton';

const providerLabel = (provider?: string | null) => {
  const key = (provider || '').trim().toLowerCase();
  if (key === 'paytr') return 'PayTR';
  if (key === 'moka') return 'Moka';
  if (key === 'parampos') return 'Param';
  return provider?.trim() || '';
};

/** Sağlayıcının bildirim (callback) adresi. PayTR eski yolunu kullanır. */
const callbackUrlFor = (firebaseId: string, provider?: string | null) => {
  const key = (provider || 'paytr').trim().toLowerCase();
  if (key === 'paytr') return `${PAYMENT_SERVICE_URL}/api/paytrv2/callback/${firebaseId}`;
  return `${PAYMENT_SERVICE_URL}/api/payments/callback/${key}/${firebaseId}`;
};

interface IssuerForm {
  id?: number;
  linkedCompanyId: string;
  title: string;
  logoUrl: string;
  isActive: boolean;
}

const emptyForm: IssuerForm = {
  linkedCompanyId: '',
  title: '',
  logoUrl: '',
  isActive: true,
};

const IssuersTab: React.FC = () => {
  const [rows, setRows] = React.useState<InvoiceIssuerCompany[]>([]);
  const [companies, setCompanies] = React.useState<Company[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [form, setForm] = React.useState<IssuerForm>(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<InvoiceIssuerCompany | null>(null);
  const [toast, setToast] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const selectedCompany = React.useMemo(
    () => companies.find((c) => c.id === form.linkedCompanyId) || null,
    [companies, form.linkedCompanyId]
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [issuers, comps] = await Promise.all([
        BillingService.getIssuers(true),
        SuperAdminService.getInstance().getAllCompanies().catch(() => [] as Company[]),
      ]);
      setRows(issuers);
      setCompanies(Array.isArray(comps) ? comps : []);
    } catch (e: any) {
      setError(e?.message || 'Fatura şirketleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };
  const openEdit = (i: InvoiceIssuerCompany) => {
    setForm({
      id: i.id,
      linkedCompanyId: i.linkedCompanyId ?? '',
      title: i.title ?? '',
      logoUrl: i.logoUrl ?? '',
      isActive: i.isActive,
    });
    setDialogOpen(true);
  };

  const copyCallback = async (firebaseId: string, provider?: string | null) => {
    try {
      await navigator.clipboard.writeText(callbackUrlFor(firebaseId, provider));
      setToast('Callback adresi kopyalandı.');
    } catch {
      setError('Kopyalanamadı.');
    }
  };

  const providerForCompany = (companyId: string) =>
    rows.find((r) => r.linkedCompanyId === companyId)?.linkedPaymentProvider || null;

  const handleSave = async () => {
    if (!form.linkedCompanyId) {
      setError('Bir Aquasoft panel şirketi seçmelisiniz.');
      return;
    }
    setSaving(true);
    try {
      const payload: SaveInvoiceIssuerCompany = {
        id: form.id,
        linkedCompanyId: form.linkedCompanyId,
        title: form.title.trim() || (selectedCompany?.name ?? ''),
        logoUrl: form.logoUrl.trim() || null,
        isActive: form.isActive,
      };
      await BillingService.saveIssuer(payload);
      setDialogOpen(false);
      setToast('Fatura şirketi kaydedildi.');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await BillingService.deleteIssuer(deleteTarget.id);
      setToast('Silindi.');
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Silinemedi.');
    }
  };

  const columns = React.useMemo<DataTableColumn<InvoiceIssuerCompany>[]>(() => [
    { id: 'sn', label: 'SN', width: '56px', align: 'center', render: (_row, index) => index + 1 },
    { id: 'title', label: 'Unvan', minWidth: '180px', render: (i) => i.title },
    { id: 'linked', label: 'Bağlı Şirket', minWidth: '160px', render: (i) => i.linkedCompanyName || '—' },
    { id: 'email', label: 'E-posta', minWidth: '180px', render: (i) => i.email || '—' },
    { id: 'tax', label: 'VKN', render: (i) => i.taxNumber || '—' },
    { id: 'status', label: 'Durum', render: (i) => (i.isActive ? 'Aktif' : 'Pasif') },
    {
      id: 'payment',
      label: 'Ödeme',
      minWidth: '120px',
      render: (i) => {
        const active = i.linkedPaymentProvider || (i.linkedPaytrReady ? 'paytr' : null);
        return active ? (
          <Chip size="small" color="success" icon={<CheckCircleIcon />} label={providerLabel(active)} />
        ) : (
          <Chip size="small" color="warning" icon={<WarningAmberIcon />} label="Yok" />
        );
      },
    },
    {
      id: 'actions',
      label: 'İşlemler',
      width: '130px',
      align: 'center',
      render: (i) => (
        <Box onClick={(e) => e.stopPropagation()}>
          {i.linkedCompanyId && i.linkedPaymentProvider && (
            <Tooltip title={`${providerLabel(i.linkedPaymentProvider)} callback adresini kopyala`}>
              <IconButton size="small" onClick={() => copyCallback(i.linkedCompanyId!, i.linkedPaymentProvider)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Düzenle">
            <IconButton size="small" color="primary" onClick={() => openEdit(i)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sil">
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(i)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#1976d2', color: 'white', px: 2, py: 1, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{rows.length} fatura şirketi</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
            Yeni Şirket
          </Button>
          <GuideButton guideKey="superadmin.billingIssuers" />
        </Box>
      </Stack>

      <AccountingDataTable<InvoiceIssuerCompany>
        columns={columns}
        rows={rows}
        getRowId={(i) => String(i.id)}
        loading={loading}
        ariaLabel="Fatura şirketleri"
        emptyMessage="Henüz fatura şirketi eklenmedi."
        defaultRowsPerPage={25}
        onRowClick={(i) => openEdit(i)}
        clickable
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="span" sx={{ flex: 1 }}>{form.id ? 'Fatura Şirketini Düzenle' : 'Yeni Fatura Şirketi'}</Box>
          <GuideButton guideKey="superadmin.billingIssuers" size="small" />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete<Company>
              options={companies}
              getOptionLabel={(c) => {
                const email = c.admin?.email?.trim();
                const name = c.name || c.id;
                return email ? `${name} — ${email}` : name;
              }}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={selectedCompany}
              onChange={(_e, val) =>
                setForm((f) => ({
                  ...f,
                  linkedCompanyId: val?.id ?? '',
                  title: f.title.trim() ? f.title : (val?.name ?? ''),
                }))
              }
              renderOption={(props, c) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                    <Typography variant="body2">{c.name || c.id}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.admin?.email?.trim() || 'E-posta yok'}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Panel Şirketi" required placeholder="Şirket adı veya e-posta ara..." />
              )}
            />

            {selectedCompany && (
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2"><b>Şirket:</b> {selectedCompany.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    <b>Yetkili:</b> {selectedCompany.admin?.name || '—'} · {selectedCompany.admin?.email || '—'}
                    {selectedCompany.admin?.phone ? ` · ${selectedCompany.admin.phone}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    VKN / vergi dairesi / adres, fatura kesilirken panel şirketinden otomatik alınır.
                  </Typography>
                  {providerForCompany(selectedCompany.id) ? (
                    <TextField
                      label={`${providerLabel(providerForCompany(selectedCompany.id))} bildirim adresi`}
                      value={callbackUrlFor(selectedCompany.id, providerForCompany(selectedCompany.id))}
                      fullWidth
                      size="small"
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Kopyala">
                              <IconButton
                                size="small"
                                onClick={() => copyCallback(selectedCompany.id, providerForCompany(selectedCompany.id))}
                                edge="end"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mt: 1 }}
                      helperText="Bu adres sağlayıcı panelinde bildirim URL olmalıdır. Kayıt yalnızca şubeye özelse burada görünmez; şirket geneli entegrasyon gerekir."
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Ödeme sistemi, panel şirketinin şirket geneli PayTR veya Moka kaydından okunur ve
                      kayıt sonrası listede görünür. Şubeye özel entegrasyon tahsilata dahil edilmez.
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}

            <TextField
              label="Fatura Unvanı (opsiyonel)"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              fullWidth
              helperText="Boş bırakılırsa panel şirketinin adı kullanılır."
            />
            <TextField
              label="Logo URL (opsiyonel)"
              value={form.logoUrl}
              onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Aktif"
            />

            <Divider />
            <Typography variant="caption" color="text.secondary">
              Ödeme bilgisi (Merchant ID, Moka bayi kodu vb.) fatura şirketine yazılmaz. Liste,
              bağlı panel şirketinin aktif PayTR veya Moka kaydını gösterir.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.linkedCompanyId}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Şirketi sil</DialogTitle>
        <DialogContent>
          <b>{deleteTarget?.title}</b> silinsin mi?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>İptal</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}>
        <Alert severity="success" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IssuersTab;
